import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { Users } from "lucide-react";
import RankBadge from "../../components/club/RankBadge";
import { ClubApi } from "../../services/clubApi";
import { buildCasePath, slugifyText } from "../../utils/clubCasePath";
import {
  CASE_VIEW_PRIMARY_READING_CLASS as CASE_PRIMARY_READING_CLASS,
  CASE_VIEW_SUPPORTING_READING_CLASS as CASE_SUPPORTING_READING_CLASS,
} from "../../utils/caseViewTypography";
import { devLog } from "../../utils/devLog";
import ClubCaseAccessRejectedPage from "./ClubCaseAccessRejectedPage";

const toEmbedUrl = (raw = "") => {
  const value = String(raw || "").trim();
  if (!value) return "";
  if (value.includes("/embed/")) return value;

  const watchMatch = value.match(/[?&]v=([^&]+)/);
  if (watchMatch?.[1]) return `https://www.youtube.com/embed/${watchMatch[1]}`;

  const shortMatch = value.match(/youtu\.be\/([^?]+)/);
  if (shortMatch?.[1]) return `https://www.youtube.com/embed/${shortMatch[1]}`;

  return value;
};

const RANK_LABELS: Record<string, string> = {
  junior_detective: "Junior Detective",
  detective: "Detective",
  senior_detective: "Senior Detective",
  lead_detective: "Lead Detective",
  special_investigator: "Special Investigator",
};
const REGISTERED_DEMO_CASE_WALKTHROUGH_STEPS = [
  "Watch the briefing and absorb the detective tone.",
  "Open the first evidence items and begin your timeline.",
  "Return to the Agency demo when you are ready for the archive lock and upgrade path.",
];
const ClubCaseAccessPage = () => {
  const { caseIndex, caseSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [caseData, setCaseData] = useState<any>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        setErrorCode("");
        const numericCaseIndex = Number(caseIndex);
        devLog("case", "member_case_access_load_started", {
          route: window.location.pathname,
          caseIndex: numericCaseIndex,
        });
        const response = await ClubApi.getCaseAccess(numericCaseIndex);
        setCaseData(response);
        devLog("case", "member_case_access_load_succeeded", {
          route: window.location.pathname,
          caseIndex: numericCaseIndex,
          title: String(response?.briefingTitle || response?.title || ""),
          accessMode: String(response?.access?.mode || ""),
          trialAccess: Boolean(response?.access?.trialAccess),
        });
      } catch (err: any) {
        const status = err?.response?.status;
        const apiCode = err?.response?.data?.code || "";
        const apiError = err?.response?.data?.error || "Failed to load case access";
        setErrorCode(apiCode);
        setError(apiError);
        devLog("case", "member_case_access_load_failed", {
          route: window.location.pathname,
          caseIndex: Number(caseIndex),
          status,
          code: apiCode,
          message: apiError,
        });
      } finally {
        setLoading(false);
      }
    };

    if (caseIndex) load();
  }, [caseIndex]);

  const briefingVideoUrl = toEmbedUrl(caseData?.briefingVideoUrl || "");
  const displayName =
    String(user?.fullName || "").trim() ||
    String(user?.username || "").trim() ||
    "Detective";
  const firstName = String(user?.firstName || "").trim() || displayName;
  const lastName = String(user?.lastName || "").trim();
  const role =
    String(caseData?.solveStatus?.currentRankLabel || "").trim() ||
    RANK_LABELS[String(caseData?.memberRank || "")] ||
    String(caseData?.memberRank || "")
      .replaceAll("_", " ")
      .replace(/\b\w/g, (char: string) => char.toUpperCase()) ||
    "Detective";
  const currentRankKey =
    String(caseData?.solveStatus?.currentRankKey || "").trim() ||
    String(caseData?.memberRank || "").trim();

  const replaceCaseTokens = (source: string) =>
    String(source || "")
      .replace(/\{ColdCaseClubUser\}/g, displayName)
      .replace(/\{role\}/gi, role)
      .replace(/\{first\}/gi, firstName)
      .replace(/\{last\}/gi, lastName);

  const dossierLabel = lastName
    ? replaceCaseTokens("Cold Case Dossier for {role} {last}").replace(/\s{2,}/g, " ").trim()
    : "Cold Case Dossier";

  const briefingBody = replaceCaseTokens(
    caseData?.briefingBody ||
      "Welcome {ColdCaseClubUser}. Review the briefing, inspect every artifact, and build your timeline before naming a suspect."
  );

  const orderedAssets = useMemo(
    () =>
      (caseData?.assets || []).map((asset: any, index: number) => ({
        label: String(asset?.label || `Evidence ${index + 1}`),
        storageKey: String(asset?.storageKey || ""),
        contentType: String(asset?.contentType || "file"),
        sizeBytes: Number(asset?.sizeBytes || 0),
        access: asset?.access || null,
        order: index + 1,
        evidencePath: `/evidence/${String(index + 1).padStart(2, "0")}-${slugifyText(
          asset?.label || `artifact-${index + 1}`
        )}`,
      })),
    [caseData?.assets]
  );

  const mainFile = useMemo(() => {
    if (!caseData?.mainFile?.access?.url) return null;
    return {
      label: String(caseData?.mainFile?.label || "Manifest / Main File"),
      storageKey: String(caseData?.mainFile?.storageKey || ""),
      contentType: String(caseData?.mainFile?.contentType || "file"),
      access: caseData?.mainFile?.access,
    };
  }, [caseData?.mainFile]);

  const displayCaseTitle = useMemo(() => {
    const preferred = String(caseData?.briefingTitle || "").trim();
    if (preferred) return preferred;

    return String(caseData?.title || "")
      .replace(/-/g, " ")
      .trim();
  }, [caseData?.briefingTitle, caseData?.title]);

  const isTrialCase = Number(caseData?.caseIndex) === 1000;
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const registeredDemoSource = searchParams.get("demo") === "registered";
  const demoStepKey = String(searchParams.get("demoStep") || "").trim() || "free_case";
  const registeredDemoReturnStepKey =
    demoStepKey === "free_case" ? "archive_preview" : demoStepKey;
  const registeredDemoReturnPath = `/agency/dashboard?demo=registered&demoStep=${encodeURIComponent(
    registeredDemoReturnStepKey
  )}#registered-demo`;
  const generatedEvidenceFiles = Array.isArray(caseData?.casePayload?.evidence_files)
    ? caseData.casePayload.evidence_files
    : [];

  useEffect(() => {
    if (!caseData?.caseIndex) return;

    const slugSource =
      String(caseData?.briefingTitle || "").trim() ||
      String(caseData?.title || "").trim() ||
      `case-${caseData.caseIndex}`;
    const expectedSlug = slugifyText(slugSource) || `case-${caseData.caseIndex}`;
    if (caseSlug === expectedSlug) return;

    navigate(
      `${buildCasePath(caseData.caseIndex, caseData.title || "", caseData.briefingTitle || "")}${
        location.search || ""
      }`,
      { replace: true }
    );
  }, [caseData?.caseIndex, caseData?.title, caseData?.briefingTitle, caseSlug, location.search, navigate]);

  useEffect(() => {
    if (!caseData?.caseIndex) return;
    if (!generatedEvidenceFiles.length) return;
    const basePath = buildCasePath(
      caseData.caseIndex,
      caseData.title || "",
      caseData.briefingTitle || ""
    );
    const finalPath = `${basePath}/final`;
    if (window.location.pathname !== finalPath) {
      navigate(`${finalPath}${location.search || ""}`, { replace: true });
    }
  }, [
    caseData?.briefingTitle,
    caseData?.caseIndex,
    caseData?.title,
    generatedEvidenceFiles.length,
    location.search,
    navigate,
  ]);

  useEffect(() => {
    if (!isTrialCase || !registeredDemoSource) return;
    devLog("case", "member_trial_case_demo_walkthrough_started", {
      route: window.location.pathname,
      caseIndex: Number(caseData?.caseIndex || caseIndex || 0),
      demoStepKey,
    });
  }, [caseData?.caseIndex, caseIndex, demoStepKey, isTrialCase, registeredDemoSource]);

  if (loading) return <div className="p-6 text-white">Loading case...</div>;
  const showJoinPage =
    errorCode === "case_access_not_granted" ||
    errorCode === "membership_not_found" ||
    error === "Club membership not found";
  if (showJoinPage) {
    return <ClubCaseAccessRejectedPage />;
  }
  if (error) return <div className="p-6 text-red-400">{error}</div>;

  return (
    <section className="mx-auto max-w-7xl px-3 py-4 text-white sm:px-4 sm:py-5 md:p-7">
      <div className="relative overflow-hidden rounded-3xl border border-red-300/20 bg-[radial-gradient(circle_at_top_left,rgba(190,24,93,0.24),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(56,189,248,0.22),transparent_40%),linear-gradient(130deg,#08080b,#111827,#1f1417)] p-7 shadow-[0_30px_90px_rgba(5,10,30,0.6)]">
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:26px_26px] opacity-25" />
        <div className="relative">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.35em] text-cyan-200">{dossierLabel}</p>
              <h1 className="mt-3 text-3xl font-black leading-tight md:text-5xl">
                {isTrialCase
                  ? `Trial Case: ${displayCaseTitle}`
                  : `Case #${caseData?.caseIndex}: ${displayCaseTitle}`}
              </h1>
            </div>
            {!isTrialCase ? (
              <div className="flex items-center gap-3 self-start rounded-2xl border border-white/10 bg-black/20 px-4 py-3 backdrop-blur-sm">
                <RankBadge
                  rankKey={currentRankKey}
                  rankLabel={role}
                  size={64}
                  className="h-16 w-16 rounded-2xl border border-cyan-200/15 bg-white/5 p-1.5"
                />
                <div>
                  <p className="text-[11px] uppercase tracking-[0.28em] text-cyan-100/80">
                    Current Rank
                  </p>
                  <p className="mt-1 text-sm font-semibold text-white">{role}</p>
                </div>
              </div>
            ) : null}
          </div>
          {isTrialCase && (
            <p className="mt-2 inline-flex rounded-full border border-amber-300/40 bg-amber-400/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-amber-100">
              Trial Access
            </p>
          )}
          <p className={`mt-4 max-w-3xl whitespace-pre-wrap text-zinc-200 ${CASE_SUPPORTING_READING_CLASS}`}>
            {caseData?.summary}
          </p>

          <div className="mt-5 flex flex-wrap gap-2">
            <span className="rounded-full border border-cyan-300/35 bg-cyan-400/10 px-3 py-1 text-xs font-semibold text-cyan-100">
              Agent: {displayName}
            </span>
            <span className="rounded-full border border-amber-300/35 bg-amber-400/10 px-3 py-1 text-xs font-semibold text-amber-100">
              Evidence Files: {orderedAssets.length}
            </span>
            <span className="rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs text-white/90">
              Access: Signed Secure Links
            </span>
          </div>
          {isTrialCase && (
            <p className={`mt-4 max-w-3xl whitespace-pre-wrap text-amber-100/90 ${CASE_SUPPORTING_READING_CLASS}`}>
              You are viewing the free trial investigation. Join the club to unlock monthly
              member-only cases and rank progression.
            </p>
          )}
          {isTrialCase && registeredDemoSource ? (
            <div className="mt-5 max-w-3xl rounded-2xl border border-cyan-300/25 bg-cyan-400/10 p-4">
              <p className="text-[11px] uppercase tracking-[0.24em] text-cyan-100">
                Guided Walkthrough Active
              </p>
              <p className={`mt-2 text-cyan-50/95 ${CASE_SUPPORTING_READING_CLASS}`}>
                You entered Case File 000 from the Agency demo. Use this walkthrough to sample the briefing flow, then return to the demo for the archive lock and membership activation path.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Link
                  to={registeredDemoReturnPath}
                  className="rounded-lg bg-cyan-300 px-4 py-2.5 text-sm font-semibold text-[#07111f]"
                >
                  Return to Demo
                </Link>
                <Link
                  to="/agency/join?plan=digital"
                  className="rounded-lg border border-white/25 px-4 py-2.5 text-sm font-semibold"
                >
                  Become An Active Member
                </Link>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-7 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="space-y-6">
          <article className="rounded-2xl border border-white/15 bg-zinc-950/80 p-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-2xl font-bold tracking-tight">
                {caseData?.briefingTitle || "Video Briefing"}
              </h2>
              <span className="rounded-full border border-red-300/30 bg-red-500/15 px-3 py-1 text-xs font-semibold text-red-100">
                Priority Feed
              </span>
            </div>

            {briefingVideoUrl ? (
              <div className="overflow-hidden rounded-xl border border-white/15 shadow-[0_15px_45px_rgba(0,0,0,0.45)]">
                <iframe
                  title="Case briefing video"
                  src={briefingVideoUrl}
                  className="aspect-video w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-white/30 bg-white/5 p-5 text-sm text-zinc-300">
                Add a briefing video URL in admin for this case.
              </div>
            )}
          </article>

          <article className="rounded-2xl border border-cyan-300/20 bg-cyan-500/10 p-5">
            <h3 className="text-xl font-bold text-cyan-50">Briefing Notes</h3>
            <p className={`mt-3 whitespace-pre-wrap text-cyan-50/95 ${CASE_PRIMARY_READING_CLASS}`}>
              {briefingBody}
            </p>
          </article>

          {isTrialCase && registeredDemoSource ? (
            <article className="rounded-2xl border border-[#202c3f] bg-[#0b1320] p-5">
              <p className="text-[11px] uppercase tracking-[0.24em] text-[#c6b894]">
                Demo Case Walkthrough
              </p>
              <h3 className="mt-3 text-xl font-bold text-[#efede7]">
                {firstName}, follow this investigation path.
              </h3>
              <ol className="mt-4 space-y-3">
                {REGISTERED_DEMO_CASE_WALKTHROUGH_STEPS.map((step, index) => (
                  <li
                    key={`registered-demo-case-step-${index}`}
                    className="rounded-2xl border border-[#222938] bg-[#111722] p-3 text-sm text-[#d8d2c4]"
                  >
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#c6b894]">
                      Walkthrough Step {index + 1}
                    </p>
                    <p className="mt-1">{step}</p>
                  </li>
                ))}
              </ol>
              <div className="mt-4 flex flex-wrap gap-3">
                <a
                  href={mainFile?.access?.url || orderedAssets[0]?.access?.url || "#evidence-index"}
                  className="rounded-lg bg-amber-300 px-4 py-2.5 text-sm font-semibold text-[#1f1602]"
                >
                  Open First Evidence
                </a>
                <Link
                  to={registeredDemoReturnPath}
                  className="rounded-lg border border-white/25 px-4 py-2.5 text-sm font-semibold"
                >
                  Back to Demo
                </Link>
              </div>
            </article>
          ) : null}
        </div>

        <aside className="rounded-2xl border border-amber-300/25 bg-gradient-to-b from-amber-500/12 to-zinc-950/80 p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-lg font-bold text-amber-100">Evidence Index</h3>
            <Link
              to={buildCasePath(
                caseData?.caseIndex,
                caseData?.title || "",
                caseData?.briefingTitle || ""
              )}
              className="text-xs font-semibold text-amber-100/90 underline underline-offset-4"
            >
              Canonical Path
            </Link>
          </div>

          {mainFile ? (
            <div className="mb-3 rounded-xl border border-cyan-300/30 bg-cyan-500/10 p-3">
              <p className="text-[11px] uppercase tracking-[0.2em] text-cyan-100/85">
                Primary Evidence
              </p>
              <a
                href={mainFile.access.url}
                target="_blank"
                rel="noreferrer"
                className="mt-1 block text-sm font-semibold text-cyan-100 underline decoration-cyan-200/65 underline-offset-4 hover:text-white"
              >
                {mainFile.label}
              </a>
            </div>
          ) : null}

          {orderedAssets.length === 0 ? (
            <p className="mt-3 text-sm text-amber-50/80">No artifacts available yet.</p>
          ) : (
            <ul className="space-y-3">
              {orderedAssets.map((asset: any) => (
                <li
                  key={asset.storageKey}
                  className="rounded-xl border border-white/15 bg-black/25 p-3"
                >
                  <p className="text-[11px] uppercase tracking-[0.2em] text-amber-200/80">
                    Evidence {String(asset.order).padStart(2, "0")}
                  </p>
                  <a
                    href={asset?.access?.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 block text-sm font-semibold text-amber-100 underline decoration-amber-300/60 underline-offset-4 hover:text-white"
                  >
                    {asset.label}
                  </a>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 rounded-xl border border-white/15 bg-white/5 p-3 text-xs text-zinc-200">
            Tip: Start with Evidence 01 and build a timeline before jumping to later files.
          </div>
        </aside>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        {Boolean(caseData?.sessionHostingAllowed) ? (
          <Link
            to={`/agency/sessions/new?caseIndex=${encodeURIComponent(
              String(caseData?.caseIndex || "")
            )}&caseName=${encodeURIComponent(
              String(caseData?.briefingTitle || caseData?.title || "")
            )}`}
            title="Press to start a group sharing sessions with your friends."
            aria-label="Start group session"
            className="inline-flex items-center justify-center rounded-lg bg-cyan-300 p-2.5 text-[#07111f]"
          >
            <Users className="h-4 w-4" />
          </Link>
        ) : null}
        {generatedEvidenceFiles.length > 0 && (
          <Link
            to={`${buildCasePath(
              caseData?.caseIndex,
              caseData?.title || "",
              caseData?.briefingTitle || ""
            )}/final`}
            className="rounded-lg bg-amber-300 px-4 py-2.5 text-sm font-semibold text-[#1f1602]"
          >
            Open Final Case File
          </Link>
        )}
        <Link
          to={isTrialCase && registeredDemoSource ? registeredDemoReturnPath : "/agency/dashboard"}
          className="rounded-lg bg-cyan-300 px-4 py-2.5 text-sm font-semibold text-[#07111f]"
        >
          {isTrialCase && registeredDemoSource ? "Return to Demo" : "Back to Dashboard"}
        </Link>
        <Link
          to="/agency/unlock"
          className="rounded-lg border border-white/25 px-4 py-2.5 text-sm font-semibold"
        >
          Unlock Another Case
        </Link>
      </div>
    </section>
  );
};

export default ClubCaseAccessPage;
